import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  CalendarDays, FileBadge, Wallet, ScrollText, ShieldAlert,
  Star, Flag, Settings as SettingsIcon, FileText, Eye, MessageCircle,
} from "lucide-react";

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtD = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR") : "—";
const fmtEur = (n) => `${Number(n || 0).toFixed(2)}€`;

const STATUS_BG = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  completed: "bg-sky-100 text-sky-700",
  cancelled: "bg-slate-200 text-slate-600",
  no_show: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  pending: "En attente", confirmed: "Confirmée", completed: "Effectuée",
  cancelled: "Annulée", no_show: "No-show",
};
const PAY_LABEL = {
  pending: "En attente", paid: "Payé", cash_due: "Espèces dû",
  refunded: "Remboursé", failed: "Échec", cash_commission_pending: "Comm. en ligne",
};

export default function UserHistoryDialog({ userId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setData(null);
    setLoading(true);
    api.get(`/admin/users/${userId}/history`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Erreur de chargement");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [userId, open, onClose]);

  const openDoc = async (kind, id) => {
    let toastId;
    try {
      toastId = toast.loading("Chargement du document…");
      // Step 1 — resolve storage path via authenticated API
      const path = kind === "diploma" ? `/diplomas/${id}/url` : `/insurance/${id}/url`;
      const { data: r } = await api.get(path);
      if (!r?.url) {
        toast.dismiss(toastId);
        toast.error("Document introuvable");
        return;
      }
      // Step 2 — fetch the file as blob through the authenticated axios client
      // (cookies + Bearer + CSRF). This is robust against cross-origin window.open
      // limitations where the new tab loses the auth context.
      const fileResp = await api.get(r.url.replace(/^\/api/, ""), { responseType: "blob" });
      const blob = new Blob([fileResp.data], { type: r.content_type || fileResp.data.type || "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      toast.dismiss(toastId);
      const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Le navigateur a bloqué la fenêtre. Autorisez les pop-ups pour cet onglet.");
      }
      // Revoke the URL after a delay to free memory (browser will have loaded it by then)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      if (toastId) toast.dismiss(toastId);
      const detail = e?.response?.data?.detail || e?.message || "Document indisponible";
      toast.error(typeof detail === "string" ? detail : "Document indisponible");
    }
  };

  const u = data?.user;
  const stats = data?.stats || {};
  const role = u?.role;
  const nav = useNavigate();
  const goMessage = () => {
    if (!u?.user_id) return;
    onClose?.();
    nav(`/messages/${u.user_id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="user-history-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <DialogTitle data-testid="user-history-title">
                Historique complet — {u?.name || "Utilisateur"}
              </DialogTitle>
              <DialogDescription>
                {u?.email} · <span className="uppercase">{role}</span>
                {u?.suspended ? <span className="ml-2 text-red-600 font-medium">(Bloqué)</span> : null}
                · Inscrit le {fmtD(u?.created_at)}
              </DialogDescription>
            </div>
            {u && role !== "admin" && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={goMessage} data-testid="admin-send-message">
                <MessageCircle className="w-3 h-3 mr-1" />Envoyer un message
              </Button>
            )}
          </div>
        </DialogHeader>

        {loading || !data ? (
          <div className="p-12 text-center text-slate-500">Chargement de l'historique…</div>
        ) : (
          <div className="space-y-4">
            {/* Identité + KPIs */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="card-soft p-4 text-sm space-y-1">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Identité</div>
                <div><span className="text-slate-500">Civilité:</span> {u?.civility || "—"}</div>
                <div><span className="text-slate-500">Prénom / Nom:</span> {u?.first_name || "—"} {u?.last_name || ""}</div>
                <div><span className="text-slate-500">Date de naissance:</span> {fmtD(u?.birth_date)}</div>
                <div><span className="text-slate-500">Téléphone:</span> {u?.phone || "—"}</div>
                <div><span className="text-slate-500">Adresse:</span> {u?.address || "—"} {u?.postcode || ""} {u?.city || ""}</div>
                <div><span className="text-slate-500">Code parrainage:</span> {u?.referral_code || "—"} · {Number(u?.referral_credits ?? 0)} crédits</div>
                {role === "mns" && data.mns_profile && (
                  <>
                    <div className="pt-2 border-t mt-2 text-xs uppercase tracking-widest text-slate-400">Profil MNS</div>
                    <div><span className="text-slate-500">SIRET:</span> {data.mns_profile.siret || "—"}</div>
                    <div><span className="text-slate-500">TVA:</span> {data.mns_profile.vat_subject ? `Assujetti (${data.mns_profile.vat_rate || 20}%)` : "Franchise art. 293 B"}</div>
                    <div><span className="text-slate-500">NAF:</span> {data.mns_profile.activity_code_naf || "—"}</div>
                    <div><span className="text-slate-500">Tarif horaire:</span> {data.mns_profile.hourly_rate ? `${data.mns_profile.hourly_rate}€/h` : "—"}</div>
                  </>
                )}
              </div>

              <div className="card-soft p-4">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Statistiques</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <KPI label="Réservations" value={stats.bookings_total} testId="kpi-total" />
                  <KPI label="Effectuées" value={stats.completed_count} testId="kpi-completed" />
                  <KPI label="Annulées" value={stats.cancelled_count} testId="kpi-cancelled" />
                  <KPI label="No-show" value={stats.no_show_count} testId="kpi-noshow" />
                  <KPI label="En ligne" value={stats.online_count} testId="kpi-online" />
                  <KPI label="Espèces" value={stats.cash_count} testId="kpi-cash" />
                  <KPI label="Total payé" value={fmtEur(stats.total_paid_eur)} testId="kpi-paid" />
                  <KPI label="Avis donnés" value={data.reviews_given?.length || 0} testId="kpi-reviews-g" />
                  <KPI label="Avis reçus" value={data.reviews_received?.length || 0} testId="kpi-reviews-r" />
                </div>
                {role === "mns" && (
                  <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Solde portefeuille</div>
                      <div className="font-semibold">{fmtEur(stats.wallet_balance_eur)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Abonnement</div>
                      <div className="font-semibold">
                        {data.subscription
                          ? <span className="text-emerald-700">{data.subscription.plan_id} · {data.subscription.status}</span>
                          : <span className="text-slate-500">Aucun</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="bookings">
              <TabsList className="tabs-scroll bg-slate-100 p-1 rounded-xl">
                <TabsTrigger value="bookings" data-testid="uh-tab-bookings"><CalendarDays className="w-4 h-4 mr-1" />Réservations ({data.bookings?.length || 0})</TabsTrigger>
                <TabsTrigger value="cgv" data-testid="uh-tab-cgv"><ScrollText className="w-4 h-4 mr-1" />CGV/CGU ({data.cgv_acceptances?.length || 0})</TabsTrigger>
                {role === "mns" && (
                  <TabsTrigger value="docs" data-testid="uh-tab-docs"><FileBadge className="w-4 h-4 mr-1" />Documents ({(data.diplomas?.length || 0) + (data.insurance?.length || 0)})</TabsTrigger>
                )}
                {role === "mns" && (
                  <TabsTrigger value="wallet" data-testid="uh-tab-wallet"><Wallet className="w-4 h-4 mr-1" />Portefeuille ({data.wallet_transactions?.length || 0})</TabsTrigger>
                )}
                <TabsTrigger value="reviews" data-testid="uh-tab-reviews"><Star className="w-4 h-4 mr-1" />Avis</TabsTrigger>
                <TabsTrigger value="reports" data-testid="uh-tab-reports"><Flag className="w-4 h-4 mr-1" />Signalements ({(data.reports_against?.length || 0) + (data.reports_filed?.length || 0)})</TabsTrigger>
                <TabsTrigger value="sanctions" data-testid="uh-tab-sanctions"><ShieldAlert className="w-4 h-4 mr-1" />Sanctions ({data.sanctions_log?.length || 0})</TabsTrigger>
                <TabsTrigger value="settings" data-testid="uh-tab-settings"><SettingsIcon className="w-4 h-4 mr-1" />Préférences</TabsTrigger>
              </TabsList>

              <TabsContent value="bookings" className="mt-3">
                {(data.bookings || []).length === 0 ? (
                  <Empty>Aucune réservation</Empty>
                ) : (
                  <div className="card-soft overflow-x-auto">
                    <table className="w-full text-sm min-w-[820px]">
                      <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">{role === "mns" ? "Client" : "MNS"}</th>
                          <th className="text-left p-2">Lieu</th>
                          <th className="text-left p-2">Mode</th>
                          <th className="text-right p-2">Montant</th>
                          <th className="text-left p-2">Statut</th>
                          <th className="text-left p-2">Paiement</th>
                          <th className="text-left p-2">CGV</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.bookings.map((b) => (
                          <tr key={b.booking_id} className="border-t" data-testid={`uh-bk-${b.booking_id}`}>
                            <td className="p-2 whitespace-nowrap">{fmtDT(b.start_dt)}</td>
                            <td className="p-2">
                              <div className="font-medium">{b.counterpart_name || "—"}</div>
                              <div className="text-xs text-slate-400">{b.counterpart_email || ""}</div>
                            </td>
                            <td className="p-2">
                              <div>{b.pool_name || "—"}</div>
                              <div className="text-xs text-slate-400">{b.pool_address ? `${b.pool_address}${b.pool_city ? ", " + b.pool_city : ""}` : ""}</div>
                            </td>
                            <td className="p-2 text-xs">{b.payment_method === "online" ? "En ligne" : (b.payment_method === "cash" ? "Espèces" : "—")}</td>
                            <td className="p-2 text-right font-medium">{fmtEur(b.amount_eur)}</td>
                            <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BG[b.status] || "bg-slate-100"}`}>{STATUS_LABEL[b.status] || b.status}</span></td>
                            <td className="p-2 text-xs text-slate-600">{PAY_LABEL[b.payment_status] || b.payment_status || "—"}</td>
                            <td className="p-2 text-xs text-slate-600">
                              {b.cgv_accepted_at ? `v${b.cgv_version || "?"} · ${fmtD(b.cgv_accepted_at)}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="cgv" className="mt-3">
                {(data.cgv_acceptances || []).length === 0 ? (
                  <Empty>Aucune validation CGV/CGU enregistrée</Empty>
                ) : (
                  <div className="card-soft overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Version</th>
                          <th className="text-left p-2">Contexte</th>
                          <th className="text-left p-2">IP</th>
                          <th className="text-left p-2">User-Agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.cgv_acceptances.map((c) => (
                          <tr key={`${c.timestamp || ""}-${c.version || ""}-${c.context || c.kind || ""}`} className="border-t">
                            <td className="p-2 whitespace-nowrap">{fmtDT(c.timestamp)}</td>
                            <td className="p-2">{c.version || "—"}</td>
                            <td className="p-2 text-xs">{c.context || c.kind || "—"}</td>
                            <td className="p-2 text-xs font-mono">{c.ip || "—"}</td>
                            <td className="p-2 text-xs text-slate-500 truncate max-w-[280px]">{c.user_agent || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {role === "mns" && (
                <TabsContent value="docs" className="mt-3 space-y-3">
                  <DocList title="Diplômes / Cartes pro / PSE" docs={data.diplomas || []} kind="diploma" onView={openDoc} />
                  <DocList title="Assurance professionnelle" docs={data.insurance || []} kind="insurance" onView={openDoc} />
                </TabsContent>
              )}

              {role === "mns" && (
                <TabsContent value="wallet" className="mt-3">
                  {(data.wallet_transactions || []).length === 0 ? (
                    <Empty>Aucune transaction</Empty>
                  ) : (
                    <div className="card-soft overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                          <tr>
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Type</th>
                            <th className="text-left p-2">Description</th>
                            <th className="text-right p-2">Montant</th>
                            <th className="text-left p-2">Réf.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.wallet_transactions.map((t) => (
                            <tr key={t.tx_id} className="border-t">
                              <td className="p-2 whitespace-nowrap">{fmtDT(t.created_at)}</td>
                              <td className="p-2"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{t.kind}</span></td>
                              <td className="p-2 text-xs">{t.description || "—"}</td>
                              <td className={`p-2 text-right font-medium ${(t.amount || 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtEur(t.amount)}</td>
                              <td className="p-2 text-xs text-slate-500 font-mono">{t.ref || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
              )}

              <TabsContent value="reviews" className="mt-3 space-y-4">
                <ReviewList title={role === "mns" ? "Avis reçus" : "Avis reçus"} list={data.reviews_received || []} />
                <ReviewList title="Avis donnés" list={data.reviews_given || []} />
              </TabsContent>

              <TabsContent value="reports" className="mt-3 space-y-4">
                <ReportList title={`Signalements contre ce compte (${data.reports_against?.length || 0})`} list={data.reports_against || []} />
                <ReportList title={`Signalements émis par ce compte (${data.reports_filed?.length || 0})`} list={data.reports_filed || []} />
              </TabsContent>

              <TabsContent value="sanctions" className="mt-3">
                {(data.sanctions_log || []).length === 0 ? (
                  <Empty>Aucune action admin</Empty>
                ) : (
                  <div className="card-soft overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Action</th>
                          <th className="text-left p-2">Cible</th>
                          <th className="text-left p-2">Détails</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.sanctions_log.map((s) => (
                          <tr key={s.audit_id || `${s.created_at}-${s.action}-${s.target_id}`} className="border-t">
                            <td className="p-2 whitespace-nowrap">{fmtDT(s.created_at)}</td>
                            <td className="p-2"><span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{s.action}</span></td>
                            <td className="p-2 text-xs">{s.target_type}: {s.target_id}</td>
                            <td className="p-2 text-xs text-slate-500">{s.details ? JSON.stringify(s.details) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="settings" className="mt-3">
                {!data.settings ? (
                  <Empty>Aucune préférence enregistrée</Empty>
                ) : (
                  <div className="card-soft p-4 text-sm space-y-2">
                    <div><strong>Notifications activées:</strong> {(data.settings.kinds || []).join(", ") || "—"}</div>
                    <div><strong>Marketing consent:</strong> {data.settings.marketing_consent ? "Oui" : "Non"}</div>
                    <div><strong>Marketing email:</strong> {data.settings.marketing_email || "—"}</div>
                    <div className="pt-2 border-t">
                      <strong>Données brutes:</strong>
                      <pre className="text-xs bg-slate-50 p-2 rounded mt-1 overflow-x-auto">{JSON.stringify(data.settings, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const KPI = ({ label, value, testId }) => (
  <div className="px-2 py-1" data-testid={testId}>
    <div className="text-lg font-semibold">{value ?? 0}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
  </div>
);

const Empty = ({ children }) => (
  <div className="card-soft p-8 text-center text-slate-500">{children}</div>
);

function DocList({ title, docs, kind, onView }) {
  if (!docs.length) return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title}</h4>
      <Empty>Aucun document</Empty>
    </div>
  );
  const STATUS_BG_DOC = {
    pending: "bg-amber-100 text-amber-800",
    validated: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    expired: "bg-slate-300 text-slate-700",
  };
  return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title}</h4>
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Fichier</th>
              <th className="text-left p-2">Uploadé</th>
              <th className="text-left p-2">Expire</th>
              <th className="text-left p-2">Statut</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => {
              const id = d.diploma_id || d.insurance_id || d.id;
              return (
                <tr key={id} className="border-t">
                  <td className="p-2 text-xs">{d.doc_type || (kind === "insurance" ? "insurance" : "diplôme")}</td>
                  <td className="p-2 text-xs text-slate-500 truncate max-w-[200px]">{d.filename || d.original_name || "—"}</td>
                  <td className="p-2 text-xs">{fmtD(d.created_at || d.uploaded_at)}</td>
                  <td className="p-2 text-xs">{fmtD(d.valid_until)}</td>
                  <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BG_DOC[d.status] || "bg-slate-100"}`}>{d.status || "—"}</span></td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => onView(kind, id)} data-testid={`uh-doc-view-${id}`}>
                      <Eye className="w-3 h-3 mr-1" />Voir
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewList({ title, list }) {
  if (!list.length) return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title}</h4>
      <Empty>Aucun avis</Empty>
    </div>
  );
  return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title} ({list.length})</h4>
      <div className="space-y-2">
        {list.map((r) => (
          <div key={r.review_id || r._id || `${r.client_id}-${r.created_at}`} className="card-soft p-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {/* Fixed 5-star display; stable keys allowed since list neither
                    reorders nor changes length. */}
                {[1, 2, 3, 4, 5].map((pos) => (
                  <Star key={`star-${pos}`} className={`w-4 h-4 ${pos <= (r.rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                ))}
                <span className="text-xs text-slate-500 ml-2">{fmtD(r.created_at)}</span>
              </div>
              <span className="text-xs text-slate-400">{r.booking_id || ""}</span>
            </div>
            {r.comment && <p className="text-slate-600 mt-1 text-sm">{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportList({ title, list }) {
  if (!list.length) return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title}</h4>
      <Empty>Aucun signalement</Empty>
    </div>
  );
  return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title}</h4>
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Cible</th>
              <th className="text-left p-2">Motif</th>
              <th className="text-left p-2">Détails</th>
              <th className="text-left p-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.report_id} className="border-t">
                <td className="p-2 whitespace-nowrap">{fmtDT(r.created_at)}</td>
                <td className="p-2 text-xs">{r.target_type}: {r.target_label || r.target_id}</td>
                <td className="p-2 text-xs">{r.reason}</td>
                <td className="p-2 text-xs text-slate-500 max-w-[280px] truncate">{r.custom_text || "—"}</td>
                <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
